import {useCallback, useEffect, useRef, useState} from 'react';
import {Animated, Easing} from 'react-native';

/**
 * Drives a panel/palette that:
 *   - opens when a "ready" signal transitions false -> true (e.g. user lands),
 *   - auto-collapses after a delay while open,
 *   - can be toggled open/closed imperatively.
 *
 * Returns the open state, open/close helpers, and an Animated progress value
 * (0 = collapsed, 1 = open) so callers can drive their own slide/fade.
 *
 * This is the same interaction as the top-left Unit detail box
 * (components/World/UnitHeaderOverlay), extracted so it can be reused.
 *
 * @param {object}  options
 * @param {boolean} options.reopenSignal    Re-opens on a false -> true edge.
 * @param {number}  options.autoCollapseMs  Delay before auto-collapsing.
 * @param {boolean} options.initialOpen     Initial open state.
 */
const useAutoCollapsePanel = ({
  reopenSignal = false,
  autoCollapseMs = 5000,
  initialOpen = true,
} = {}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const openProgress = useRef(new Animated.Value(initialOpen ? 1 : 0)).current;
  const collapseTimerRef = useRef(null);
  const prevReopenSignalRef = useRef(reopenSignal);

  // Animate progress whenever the open state flips.
  useEffect(() => {
    Animated.timing(openProgress, {
      toValue: isOpen ? 1 : 0,
      duration: isOpen ? 280 : 340,
      easing: isOpen
        ? Easing.out(Easing.cubic)
        : Easing.bezier(0.22, 1, 0.36, 1),
      useNativeDriver: true,
    }).start();
  }, [isOpen, openProgress]);

  // Re-open on the ready signal's rising edge.
  useEffect(() => {
    if (!prevReopenSignalRef.current && reopenSignal) {
      setIsOpen(true);
    }
    prevReopenSignalRef.current = reopenSignal;
  }, [reopenSignal]);

  // Auto-collapse after the delay while open.
  useEffect(() => {
    if (!isOpen) {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      return undefined;
    }

    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
    }
    collapseTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, autoCollapseMs);

    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [isOpen, autoCollapseMs]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return {isOpen, open, close, openProgress};
};

export default useAutoCollapsePanel;
